import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView } from '@tiptap/pm/view';
import { apiUpload } from '../../lib/api';

/**
 * Images are deliberately EXCLUDED here. They already have their own inline
 * flow (#214) which renders the picture itself, and a screenshot is more
 * useful seen than named. Returning false for them lets the drop/paste event
 * fall through to that handler untouched.
 */
const IMAGE_MIME_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
]);

/** True when this file should become an attachment chip rather than an image. */
export function isAttachmentCandidate(file: File): boolean {
  return !IMAGE_MIME_TYPES.has(file.type);
}

/** The non-image files from a drop or paste. Empty means "not ours". */
export function getAttachmentFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const out: File[] = [];
  for (let i = 0; i < dt.files.length; i++) {
    const f = dt.files[i];
    if (isAttachmentCandidate(f)) out.push(f);
  }
  return out;
}

export interface AttachmentUploadOptions {
  /** Owning scratchpad. Without it the extension does nothing. */
  scratchpadId?: string;
  onError?: (message: string) => void;
}

const key = new PluginKey('attachmentUploadPlaceholder');

interface PlaceholderMeta {
  add?: { id: symbol; pos: number; filename: string };
  remove?: { id: symbol };
}

interface UploadedAttachment {
  id: string;
  filename: string;
  mime_type: string;
}

/** Position of a placeholder by id, mapped through subsequent doc changes. */
function findPlaceholder(view: EditorView, id: symbol): number | null {
  const decos = key.getState(view.state) as DecorationSet | undefined;
  if (!decos) return null;
  const found = decos.find(undefined, undefined, (spec) => spec.id === id);
  return found.length ? found[0].from : null;
}

/**
 * Upload one file and swap its placeholder for a real chip.
 *
 * The placeholder is a DECORATION, never a node: transient upload state must
 * not reach the document JSON, or a failed or in-flight upload persists a
 * broken node into the saved pad (the lesson from #214).
 */
export function uploadAttachment(
  view: EditorView,
  file: File,
  insertPos: number,
  scratchpadId: string,
  onError?: (m: string) => void
): Promise<void> {
  const id = Symbol('attachment');
  view.dispatch(
    view.state.tr.setMeta(key, { add: { id, pos: insertPos, filename: file.name } } as PlaceholderMeta)
  );

  const form = new FormData();
  form.append('file', file);
  form.append('scratchpad_id', scratchpadId);

  return apiUpload<UploadedAttachment>('/attachments', form)
    .then((result) => {
      const pos = findPlaceholder(view, id);
      const tr = view.state.tr.setMeta(key, { remove: { id } } as PlaceholderMeta);
      if (pos != null) {
        const node = view.state.schema.nodes.attachmentChip.create({
          attachmentId: result.id,
          filename: result.filename,
          mimeType: result.mime_type,
        });
        tr.insert(pos, node);
      }
      view.dispatch(tr);
    })
    .catch(() => {
      // Remove the placeholder so nothing is left spinning, and leave the
      // document exactly as it was before the attempt.
      view.dispatch(view.state.tr.setMeta(key, { remove: { id } } as PlaceholderMeta));
      onError?.(`Could not attach ${file.name}. Try again.`);
    });
}

export const AttachmentUpload = Extension.create<AttachmentUploadOptions>({
  name: 'attachmentUpload',

  addOptions() {
    return { scratchpadId: undefined, onError: undefined };
  },

  addProseMirrorPlugins() {
    const getScratchpadId = () => this.options.scratchpadId;
    const onError = this.options.onError;

    return [
      new Plugin({
        key,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            set = set.map(tr.mapping, tr.doc);
            const meta = tr.getMeta(key) as PlaceholderMeta | undefined;
            if (meta?.add) {
              const el = document.createElement('span');
              el.className = 'attachment-upload-placeholder';
              el.textContent = `Attaching ${meta.add.filename}...`;
              const deco = Decoration.widget(meta.add.pos, el, { id: meta.add.id });
              set = set.add(tr.doc, [deco]);
            }
            if (meta?.remove) {
              set = set.remove(set.find(undefined, undefined, (spec) => spec.id === meta.remove!.id));
            }
            return set;
          },
        },
        props: {
          decorations(state) { return key.getState(state); },

          handleDrop(view, event, _slice, moved) {
            if (moved) return false;
            const scratchpadId = getScratchpadId();
            if (!scratchpadId) return false;
            const files = getAttachmentFiles((event as DragEvent).dataTransfer);
            if (!files.length) return false;
            event.preventDefault();
            const coords = view.posAtCoords({
              left: (event as DragEvent).clientX,
              top: (event as DragEvent).clientY,
            });
            const pos = coords?.pos ?? view.state.selection.from;
            files.forEach((f) => uploadAttachment(view, f, pos, scratchpadId, onError));
            return true;
          },

          handlePaste(view, event) {
            const scratchpadId = getScratchpadId();
            if (!scratchpadId) return false;
            const files = getAttachmentFiles((event as ClipboardEvent).clipboardData);
            if (!files.length) return false;
            event.preventDefault();
            const pos = view.state.selection.from;
            files.forEach((f) => uploadAttachment(view, f, pos, scratchpadId, onError));
            return true;
          },
        },
      }),
    ];
  },
});
